<template>
  <div :class="cardClass">
    <div class="comment-author-row">
      <div class="topic-avatar comment-avatar">{{ getInitial(comment.user) }}</div>
      <div>
        <div class="topic-author">@{{ getDisplayName(comment.user) }}</div>
        <div class="topic-date">{{ formatDate(comment.created_at) }}</div>
      </div>
    </div>

    <div class="comment-content" v-html="comment.content"></div>

    <div
      v-if="canComment && canReplyToComment"
      class="reply-action mt-4"
    >
      <button class="reply-button" type="button" @click="toggleReply">
        <Iconify icon="mdi:reply-outline" color="var(--theme-color-seven)" size="16px" />
        <span>{{ isReplying ? "ยกเลิกการตอบกลับ" : "ตอบกลับ" }}</span>
      </button>
    </div>

    <div v-if="isReplying" class="reply-form mt-4">
      <div class="reply-editor-frame">
        <Wysiwyg
          :value="replyText"
          :readonly="isSubmitting"
          height="160px"
          @onChange="updateReplyText"
        />
      </div>

      <div class="d-flex justify-end gap-12 mt-4">
        <Button
          theme="2"
          :disabled="isSubmitting"
          @onClick="$emit('close-reply', comment.id)"
        >
          ยกเลิก
        </Button>
        <Button
          theme="1"
          :loading="isSubmitting"
          @onClick="$emit('submit-reply', comment)"
        >
          <div class="d-flex align-center gap-8">
            <Iconify
              icon="mdi:reply"
              color="#FFFFFF"
              size="16px"
            />
            <span>ตอบกลับ</span>
          </div>
        </Button>
      </div>
    </div>

    <div
      v-if="comment.replies_recursive && comment.replies_recursive.length"
      class="reply-list"
    >
      <CommentItem
        v-for="reply in comment.replies_recursive"
        :key="reply.id"
        :comment="reply"
        :can-comment="canComment"
        :active-reply-id="activeReplyId"
        :reply-drafts="replyDrafts"
        :reply-loading-id="replyLoadingId"
        is-reply
        @open-reply="$emit('open-reply', $event)"
        @close-reply="$emit('close-reply', $event)"
        @update-reply-text="$emit('update-reply-text', $event)"
        @submit-reply="$emit('submit-reply', $event)"
      />
    </div>
  </div>
</template>

<script>
import Button from "@/components/input/Button.vue";
import Wysiwyg from "@/components/input/Wysiwyg.vue";

export default {
  name: "CommentItem",
  components: {
    Button,
    Wysiwyg,
  },
  props: {
    comment: {
      type: Object,
      required: true,
    },
    canComment: {
      type: Boolean,
      default: false,
    },
    activeReplyId: {
      type: Number,
      default: null,
    },
    replyDrafts: {
      type: Object,
      default: () => ({}),
    },
    replyLoadingId: {
      type: Number,
      default: null,
    },
    isReply: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    cardClass() {
      return this.isReply ? "reply-card" : "comment-card";
    },
    isReplying() {
      return this.activeReplyId === this.comment.id;
    },
    replyText() {
      return this.replyDrafts[this.comment.id] || "";
    },
    isSubmitting() {
      return this.replyLoadingId === this.comment.id;
    },
    canReplyToComment() {
      return Number(this.comment.depth || 1) < 3;
    },
  },
  methods: {
    toggleReply() {
      if (this.isReplying) {
        this.$emit("close-reply", this.comment.id);
        return;
      }

      this.$emit("open-reply", this.comment.id);
    },
    updateReplyText(value) {
      this.$emit("update-reply-text", {
        id: this.comment.id,
        value,
      });
    },
    getDisplayName(user) {
      if (!user) return "-";
      return user.name || [user.firstname, user.lastname].filter(Boolean).join(" ") || "-";
    },
    getInitial(user) {
      return (this.getDisplayName(user) || "W").toString().charAt(0).toUpperCase();
    },
    formatDate(date) {
      return this.$moment(date).add(543, "year").format("DD MMMM YYYY HH:mm");
    },
  },
};
</script>

<style scoped lang="scss">
.comment-card,
.reply-card {
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 8px 24px rgba(70, 70, 71, 0.08);
  padding: 20px 22px;
}

.comment-card {
  border-left: 3px solid #ffb66f;
}

.reply-card {
  background: #fffaf4;
  box-shadow: none;
  border: 1px solid #f5dcc1;
}

.comment-author-row {
  display: flex;
  align-items: flex-start;
  gap: 14px;
}

.topic-avatar {
  width: 30px;
  min-width: 30px;
  height: 30px;
  border-radius: 50%;
  background: linear-gradient(180deg, #ff8a4c 0%, #f15a29 100%);
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  font-family: var(--font-bold-type);
}

.comment-avatar {
  width: 28px;
  min-width: 28px;
  height: 28px;
}

.topic-author {
  font-size: 12px;
  font-family: var(--font-bold-type);
  color: var(--theme-color-two);
}

.topic-date {
  font-size: 11px;
  color: var(--theme-color-eight);
}

.comment-content {
  margin-top: 16px;
  color: var(--theme-color-two);
  line-height: 1.9;
}

.reply-action {
  display: flex;
}

.reply-button {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: transparent;
  border: 0;
  color: var(--theme-color-seven);
  font-family: var(--font-bold-type);
  cursor: pointer;
  padding: 0;
}

.reply-form {
  background: #fffaf4;
  border: 1px solid #f5dcc1;
  border-radius: 12px;
  padding: 14px;
}

.reply-editor-frame {
  border: 1px solid #f5dcc1;
  border-radius: 16px;
  overflow: hidden;
  background: #fff;
}

.reply-list {
  margin-top: 16px;
  display: flex;
  flex-direction: column;
  gap: 10px;
}
</style>
